import { Button, Col, Icon, Row, Sidepanel, Text } from 'components';
import { Api, POLL_STATE, POLL_TYPE, theme } from 'lib';
import useTeamChannelUpdates from 'core/useTeamChannelUpdates';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const BarTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #fff;
  overflow: hidden;
  margin-top: 6px;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  width: ${(p) => p.pct}%;
  background: ${(p) => (p.winner ? theme.GREEN : theme.SECONDARY)};
  transition: width 0.3s ease;
`;

const OptionRow = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid ${(p) => (p.selected ? theme.SECONDARY : theme.GREY_20)};
  background: ${(p) => (p.selected ? `${theme.SECONDARY}10` : '#fff')};
  border-radius: 10px;
  padding: 12px 14px;
  cursor: ${(p) => (p.interactive ? 'pointer' : 'default')};
  font-family: CeraPro;
`;

const RadioDot = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.selected ? theme.SECONDARY : theme.GREY_30)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &::after {
    content: '';
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(p) => (p.selected ? theme.SECONDARY : 'transparent')};
  }
`;

/**
 * Fan poll viewer + voter for the web Team Channel surface.
 *
 *  - OPEN & not yet voted → interactive options + "Vote Now" → POST /polls/submit,
 *    then lock and show the points earned + results.
 *  - voted / CLOSED       → results only (bars/%), the fan's pick highlighted.
 *  - HIDDEN               → never reaches here (the row filters hidden polls out).
 *
 * Votes can't be changed. After a successful vote we re-fetch /polls/get to pull the
 * fresh results + state, and bubble the update up via onVoted so the card matches.
 */
const PollViewModal = ({ visible = true, poll, teamId, onClose, onVoted }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [local, setLocal] = useState(null); // post-vote overrides merged onto `poll`

  useEffect(() => {
    if (!visible) return;
    setSelectedId(poll?.selectedOptionId || null);
    setSubmitting(false);
    setLocal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, poll?._id]);

  // Live results — merge every vote's breakdown while the modal is open. Only matters
  // once the fan can see results (after they vote / once closed); the render gates
  // visibility, so merging early never leaks an open poll's tally.
  useTeamChannelUpdates(
    visible ? teamId : null,
    (evt) => {
      if (!evt || evt.entity !== 'poll' || evt.id !== poll?._id) return;
      // Poll removed under us → the viewer can't show anything meaningful; close it.
      if (evt.action === 'deleted') {
        onClose && onClose();
        return;
      }
      if (evt.action === 'results_updated' && evt.data) {
        setLocal((prev) => ({
          ...(prev || {}),
          results: evt.data,
          totalSubmissions: evt.data.totalSubmissions
        }));
        return;
      }
      // Surface-state flip (e.g. poll just closed, or hidden at half-time). Merge ONLY the
      // resolved `state` — never the whole raw doc, which lacks the fan-scoped
      // answered/selectedOptionId/results fields. A poll leaving the surface (hidden)
      // shouldn't linger in an open viewer, so close it.
      if (evt.action === 'state_changed' && evt.data?.state) {
        if (evt.data.state === POLL_STATE.HIDDEN) {
          onClose && onClose();
          return;
        }
        setLocal((prev) => ({ ...(prev || {}), state: evt.data.state }));
      }
    }
  );

  if (!poll) {
    return <Sidepanel visible={visible} wid={520} pad="0" closeModal={onClose} />;
  }

  const merged = { ...poll, ...(local || {}) };
  const state = merged.state;
  const answered = merged.answered === true;
  const results = merged.results || null;
  const isGameDay = merged.pollType === POLL_TYPE.GAME_DAY;
  const canVote = state === POLL_STATE.OPEN && !answered;
  const showResults = (answered || state === POLL_STATE.CLOSED) && !!results;

  const breakdownById = new Map((results?.options || []).map((o) => [o.optionId, o]));
  const maxCount = (results?.options || []).reduce((m, o) => Math.max(m, o.count), 0);
  const totalSubmissions = results?.totalSubmissions ?? merged.totalSubmissions ?? 0;

  const submit = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    const res = await Api.post(
      '/polls/submit',
      { pollId: poll._id, optionId: selectedId },
      null,
      { silent: true }
    );

    if (res && res.err && res.status) {
      // 404 deleted, 400 closed / invalid / already-voted.
      dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
      setSubmitting(false);
      return;
    }

    const earnedPoints = res?.earnedPoints ?? 0;
    const picked = res?.selectedOptionId || selectedId;

    // Pull fresh state + results now that the fan has voted.
    const fresh = await Api.get(`/polls/get/${poll._id}`, null, { silent: true });
    const next = {
      answered: true,
      selectedOptionId: picked,
      earnedPoints,
      results: fresh?.results || results,
      state: fresh?.state || state,
      totalSubmissions: fresh?.results?.totalSubmissions ?? totalSubmissions
    };
    setLocal(next);
    setSubmitting(false);
    dispatch({
      type: 'HANDLE_RES',
      payload: {
        res: earnedPoints
          ? t('pollQuizProfile.toastVoteSubmittedPts', { points: earnedPoints })
          : t('pollQuizProfile.toastVoteSubmitted')
      }
    });
    onVoted && onVoted({ ...poll, ...next });
  };

  const earned = local?.earnedPoints ?? merged.userResponse?.earnedPoints;

  return (
    <Sidepanel visible={visible} wid={520} pad="0" closeModal={onClose}>
      <Col wid="100%" pad="0">
        <Row between center pad="16px 20px" style={{ borderBottom: `1px solid ${theme.GREY_20}` }}>
          <Text h3>{t('pollQuizProfile.modalPoll')}</Text>
          <Icon type="close" dimensions={16} onClick={onClose} style={{ cursor: 'pointer' }} />
        </Row>

        <Col wid="100%" pad="18px 20px 24px" gap="6px">
          <Row noFlex center gap="8px" marg="0 0 6px">
            <Text c2 col={theme.GREY_50}>
              {isGameDay ? t('pollQuizProfile.gameDayPoll') : t('pollQuizProfile.pollLabel')}
            </Text>
            {state === POLL_STATE.OPEN && (
              <Text c2 bold col={theme.GREEN}>
                · {t('pollQuizProfile.statusOpen')}
              </Text>
            )}
            {state === POLL_STATE.CLOSED && (
              <Text c2 col={theme.GREY_60}>
                · {t('pollQuizProfile.statusPollClosed')}
              </Text>
            )}
          </Row>

          <Text h4 marg="0 0 14px">
            {merged.question}
          </Text>

          <Col wid="100%" gap="10px">
            {(merged.options || []).map((o) => {
              const isSelected = merged.selectedOptionId === o._id;
              const isPicking = canVote && selectedId === o._id;
              const b = breakdownById.get(o._id);
              const pct = b?.percentage ?? 0;
              const winner = showResults && maxCount > 0 && (b?.count ?? 0) === maxCount;
              return (
                <OptionRow
                  key={o._id}
                  type="button"
                  interactive={canVote}
                  selected={isPicking || isSelected}
                  onClick={canVote ? () => setSelectedId(o._id) : undefined}
                  disabled={!canVote}
                >
                  <Row between center>
                    <Row noFlex center gap="8px" style={{ minWidth: 0 }}>
                      {canVote ? (
                        <RadioDot selected={selectedId === o._id} />
                      ) : (
                        isSelected && <Icon type="check" col={theme.SECONDARY} size={14} />
                      )}
                      <Text col={theme.GREY_80}>{o.text}</Text>
                    </Row>
                    {showResults && (
                      <Text c2 col={winner ? theme.GREEN : theme.GREY_60} marg="0 0 0 10px">
                        {pct}%
                      </Text>
                    )}
                  </Row>
                  {showResults && (
                    <BarTrack>
                      <BarFill pct={pct} winner={winner} />
                    </BarTrack>
                  )}
                </OptionRow>
              );
            })}
          </Col>

          {/* Footer — voting CTA, post-vote confirmation, or closed/hidden-results note. */}
          {canVote ? (
            <Col wid="100%" marg="16px 0 0" gap="8px">
              <Button
                text={submitting ? t('pollQuizProfile.submitting') : t('pollQuizProfile.voteNow')}
                onClick={submit}
                disabled={!selectedId || submitting}
                bg={theme.SECONDARY}
                wid="100%"
              />
              {merged.rewardPoints > 0 && (
                <Text c2 col={theme.GREY_50} center>
                  {t('pollQuizProfile.earnPtsForVoting', { count: merged.rewardPoints })}
                </Text>
              )}
            </Col>
          ) : answered ? (
            <Col
              wid="100%"
              marg="16px 0 0"
              pad="12px 14px"
              style={{ background: `${theme.GREEN}12`, borderRadius: 10 }}
            >
              <Row center gap="6px">
                <Icon type="check" col={theme.GREEN} size={14} />
                <Text c1 col={theme.GREY_80}>
                  {earned
                    ? t('pollQuizProfile.voteSubmittedConfirmPts', { points: earned })
                    : t('pollQuizProfile.voteSubmittedConfirm')}
                </Text>
              </Row>
            </Col>
          ) : state === POLL_STATE.CLOSED && !results ? (
            <Text c2 col={theme.GREY_50} marg="16px 0 0" center>
              {t('pollQuizProfile.thisPollClosed')}
            </Text>
          ) : null}

          {showResults && (
            <Text c2 col={theme.GREY_40} marg="12px 0 0">
              {t('pollQuizProfile.totalSubmissions', { count: totalSubmissions })}
            </Text>
          )}
        </Col>
      </Col>
    </Sidepanel>
  );
};

export default PollViewModal;
